import type { LDContext } from 'launchdarkly-react-client-sdk';

export const LD_INIT_TIMEOUT_SECONDS = 5;

export interface LaunchDarklyIdentityClient {
  waitForInitialization: (timeout?: number) => Promise<unknown>;
  identify: (context: LDContext) => Promise<unknown> | unknown;
  on?: (event: 'ready', listener: () => void) => void;
  off?: (event: 'ready', listener: () => void) => void;
}

export interface LaunchDarklyAuthUser {
  id?: string | null;
  email?: string | null;
  name?: string | null;
  role?: string | null;
  createdAt?: string | Date | null;
}

export interface LaunchDarklyUserData {
  subscription?: {
    planId?: string | null;
  } | null;
}

export const ANONYMOUS_LD_CONTEXT: LDContext = {
  kind: 'user',
  key: 'anonymous-user',
  anonymous: true,
};

export function isLaunchDarklyInitializationTimeout(error: unknown): boolean {
  return error instanceof Error && error.name.toLowerCase().includes('timeout');
}

export function buildLaunchDarklyContext(args: {
  user?: LaunchDarklyAuthUser | null;
  userData?: LaunchDarklyUserData | null;
  locale?: string;
}): LDContext {
  const { user, userData, locale = 'pt-BR' } = args;

  if (!user) {
    return ANONYMOUS_LD_CONTEXT;
  }

  return {
    kind: 'user',
    key: user.id || user.email || 'anonymous-user',
    email: user.email || undefined,
    name: user.name || undefined,
    role: user.role || undefined,
    plan: userData?.subscription?.planId || undefined,
    hasSubscription: !!userData?.subscription,
    locale,
    custom: {
      createdAt: user.createdAt || undefined,
      platform: 'web',
    },
  };
}

export async function identifyLaunchDarklyContext(args: {
  ldClient?: LaunchDarklyIdentityClient | null;
  user?: LaunchDarklyAuthUser | null;
  userData?: LaunchDarklyUserData | null;
  locale?: string;
  timeoutSeconds?: number;
}): Promise<boolean> {
  const { ldClient, user, userData, locale, timeoutSeconds = LD_INIT_TIMEOUT_SECONDS } = args;

  if (!ldClient) return false;

  try {
    await ldClient.waitForInitialization(timeoutSeconds);
  } catch (error) {
    if (isLaunchDarklyInitializationTimeout(error)) {
      return false;
    }

    throw error;
  }

  await ldClient.identify(
    buildLaunchDarklyContext({
      user,
      userData,
      locale,
    })
  );

  return true;
}

export function subscribeToLaunchDarklyReady(args: {
  ldClient?: LaunchDarklyIdentityClient | null;
  onReady: () => void;
}): (() => void) | null {
  const { ldClient, onReady } = args;

  if (!ldClient?.on || !ldClient.off) {
    return null;
  }

  const handleReady = () => {
    onReady();
  };

  ldClient.on('ready', handleReady);

  return () => {
    ldClient.off?.('ready', handleReady);
  };
}
